import { ALLOWED_FILE_NAMES } from '@strategy4kids/pack-format';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { MARKETPLACE_URL } from '../services/api.js';
import type { CardPackInfo } from '../lib/card-packs.js';

/**
 * The community packs this device has added from the marketplace: their metadata only. The images stay on the marketplace
 * (immutable, so the browser and the service worker cache them) and are drawn from there.
 */
interface CommunityPackStore {
  readonly packs: readonly CardPackInfo[];
  add(pack: CardPackInfo): void;
  remove(id: string): void;
}

export const MAX_COMMUNITY_PACKS = 24;

/** What a stored entry must look like to be trusted: it is used as an image URL, so anything off the marketplace is dropped. */
export function isStoredPack(p: unknown, marketplaceUrl: string = MARKETPLACE_URL): p is CardPackInfo {
  if (typeof p !== 'object' || p === null) return false;
  const x = p as Partial<CardPackInfo>;
  return (
    typeof x.id === 'string' && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(x.id) &&
    typeof x.name === 'string' && typeof x.tagline === 'string' &&
    x.family === 'community' &&
    Array.isArray(x.editions) && x.editions.every((e) => e === 'kids' || e === 'adult') &&
    Array.isArray(x.files) && x.files.length > 0 && x.files.every((f) => typeof f === 'string' && ALLOWED_FILE_NAMES.includes(f)) &&
    marketplaceUrl !== '' && typeof x.base === 'string' && x.base.startsWith(`${marketplaceUrl}/api/packs/`) &&
    (x.query === undefined || /^\?edition=(kids|adult)$/.test(x.query))
  );
}

export const useCommunityPackStore = create<CommunityPackStore>()(
  persist(
    (set) => ({
      packs: [],
      add(pack) {
        set((s) => ({ packs: [...s.packs.filter((p) => p.id !== pack.id), pack].slice(-MAX_COMMUNITY_PACKS) }));
      },
      remove(id) {
        set((s) => ({ packs: s.packs.filter((p) => p.id !== id) }));
      },
    }),
    {
      name: 's4k:community-packs',
      // Whatever is in storage is data a person could have edited: keep only well-formed entries for this marketplace.
      merge: (stored, current) => {
        const packs = (stored as { packs?: unknown } | undefined)?.packs;
        return { ...current, packs: Array.isArray(packs) ? packs.filter((p) => isStoredPack(p)).slice(0, MAX_COMMUNITY_PACKS) : [] };
      },
    },
  ),
);
